<template>
    <div
        :class="[$style['mds-tooltip'], classObject, sizeClass, variationClass, positionClass]"
        role="tooltip"
        :aria-hidden="!isVisible"
        :data-mds-version="version"
    >
        <slot>{{ text }}</slot>

        <div v-if="variation === 'prompt'" :class="$style['mds-tooltip__close-button']">
            <mds-button
                type="button"
                variation="icon-only"
                size="small"
                icon="remove"
                :text="dismissIconAriaLabel"
                onDark
                @click="hide"
            />
        </div>
    </div>
</template>

<script>
    import { MdsPositioning } from '@mds/utils-js';
    import { MdsButton } from '@mds/button';

    import MdsTooltipProps from './tooltip_props';

    export default {
        name: 'MdsTooltip',
        mixins: [MdsPositioning],
        components: {
            MdsButton,
        },
        props: MdsTooltipProps,
        data() {
            return {
                isVisible: false,
                currentPosition: this.position,
                triggerElement: null,
            };
        },
        model: {
            prop: 'visible',
            event: 'mds-tooltip-dismissed',
        },
        watch: {
            visible(visible) {
                this[visible ? 'show' : 'hide']();
            },
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-tooltip--visible']] = this.isVisible;
                obj[this.$style['mds-tooltip--fixed']] = this.fixed;

                return obj;
            },
            sizeClass() {
                return this.$style[`mds-tooltip--${this.size}`];
            },
            variationClass() {
                return this.$style[`mds-tooltip--${this.variation}`];
            },
            positionClass() {
                return this.currentPosition ? this.$style[`mds-tooltip--${this.currentPosition}`] : '';
            },
        },
        methods: {
            show() {
                this.$nextTick(function() {
                    this.updatePosition().then(() => {
                        this.isVisible = true;
                        this.$emit('mds-tooltip-opened');
                    });
                });
            },
            hide() {
                this.isVisible = false;
                this.$emit('mds-tooltip-dismissed');
            },
            updatePosition() {
                return new Promise(resolve => {
                    const { setPosition, attributes } = this.getPositionAttributes(this.position, this.autoPosition);
                    this.currentPosition = setPosition;
                    this.$el.setAttribute('style', this.formatPositionStyleAttributes(attributes));
                    this.$emit('mds-tooltip-position-updated');
                    resolve();
                }).catch(error => error);
            },
            bindEventHandlers() {
                if (this.triggerElement) {
                    window.addEventListener('resize', this.debounce(this.updatePosition, 50));

                    if (this.autoPosition) {
                        window.addEventListener('scroll', this.debounce(this.updatePosition, 50));
                    }
                }
            },
            removeEventHandlers() {
                if (this.triggerElement) {
                    window.removeEventListener('resize', this.updatePosition, true);
                    if (this.autoPosition) {
                        window.removeEventListener('scroll', this.updatePosition, true);
                    }

                    this.$emit('mds-tooltip-listeners-destroyed');
                }
            },
        },
        mounted() {
            document.body.appendChild(this.$el);
            this.triggerElement = document.getElementById(`${this.triggeredBy}`);
            this.bindEventHandlers();
            setTimeout(() => {
                if (this.visible) {
                    this.show();
                }
            }, 500);
        },
        beforeDestroy() {
            this.removeEventHandlers();
            if (this.$el.parentNode) {
                this.$el.parentNode.removeChild(this.$el);
            }
        },
    };
</script>

<style lang="scss" module>
    @import './tooltip.scss';
</style>
